import { Plus } from 'lucide-react'
import { cn } from '@teach/ui'
import type { ActiveBlockCard } from '@/features/editor/constants/blockTypeMatrix'
import { getTypeIconColor } from '@/features/editor/constants/blockTypeColors'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  card: ActiveBlockCard
  picked: boolean
  blocked: boolean
  onPick: () => void
  onAdd: () => void
}

/**
 * Строка типа в окне «Добавить блок»: наведение (или фокус) сразу показывает пример
 * справа, клик в любое место строки (или по «+», которая подсвечивается вместе с ней)
 * добавляет экран.
 */
export const AddBlockPickerRow = ({ card, picked, blocked, onPick, onAdd }: Props) => {
  const m = useMessages(messages)
  const Icon = card.icon
  return (
    <div className="group/row relative mb-1.5">
      <button
        type="button"
        disabled={blocked}
        onMouseEnter={blocked ? undefined : onPick}
        onFocus={onPick}
        // Клик в любое место строки добавляет экран; наведение — показывает пример.
        onClick={onAdd}
        className={cn(
          'flex w-full items-center gap-3 rounded-2xl py-2 pl-2 pr-12 text-left ring-1 ring-inset transition-colors disabled:cursor-not-allowed disabled:opacity-40',
          // Каждый тип — карточка на лёгкой подложке; выбранный светлее и с рамкой.
          picked ? 'bg-foreground/10 ring-foreground/15' : 'bg-foreground/[0.04] ring-transparent',
        )}
      >
        <span
          className={cn(
            'flex size-11 shrink-0 items-center justify-center transition-transform duration-200',
            getTypeIconColor(card.id),
            // Под мышкой (она же выделяет ряд) иконка и название чуть подрастают.
            picked ? 'scale-110' : null,
          )}
        >
          <Icon className="size-7" strokeWidth={1.75} />
        </span>
        <span className="min-w-0">
          <span
            className={cn(
              'block origin-left text-sm font-medium transition-transform duration-200',
              picked ? 'scale-105' : null,
            )}
          >
            {card.label}
          </span>
          <span className="block text-xs text-muted-foreground">
            {blocked ? m.blockedInPractical : card.description}
          </span>
        </span>
      </button>
      {picked && !blocked ? (
        <button
          type="button"
          onClick={onAdd}
          aria-label={m.addScreen(card.label)}
          title={m.add}
          className="absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg bg-primary text-primary-foreground transition group-hover/row:scale-110 group-hover/row:bg-brand group-hover/row:text-brand-foreground"
        >
          <Plus className="size-4" />
        </button>
      ) : null}
    </div>
  )
}
